<template>
  <div class="news-information-page">
    <!-- Banner Section -->
    <section class="banner-section">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('newsInformationPage.banner.title') }}</h1>
      </div>
    </section>

    <!-- News Section -->
    <section class="news-section">
      <div class="container">
        <template v-if="!isDetail">
          <div class="nav-tabs">
            <button v-for="(tab, index) in tabs"
                    :key="index"
                    class="tab-item"
                    :class="{ active: activeTab === tab.id }"
                    @click="activeTab = tab.id">
              {{ tab.name }}
            </button>
          </div>

          <div class="news-grid">
            <div class="news-item"
                 v-for="(item, index) in visibleNews"
                 :key="index"
                 @click="openDetail(item)">
              <div class="news-cover">
                <img :src="item.cover"
                     :alt="item.title"
                     class="cover-image">
              </div>
              <div class="news-content">
                <h3 class="news-title">{{ item.title }}</h3>
                <p class="news-time">{{ item.time }}</p>
              </div>
            </div>
          </div>

          <div class="pagination"
               v-if="totalPages > 1">
            <button v-for="p in totalPages"
                    :key="p"
                    class="page-number"
                    :class="{ active: currentPage === p }"
                    @click="currentPage = p">
              {{ p }}
            </button>
          </div>
        </template>

        <template v-else>
          <div class="news-detail">
            <div class="detail-actions">
              <button class="back-btn"
                      @click="goBack">
                返回
              </button>
              <div class="share-wrap">
                <input class="share-input"
                       :value="shareLink"
                       readonly>
                <button class="share-btn"
                        @click="copyShareLink">
                  复制链接
                </button>
              </div>
            </div>

            <div v-if="currentNews"
                 class="detail-body">
              <h2 class="detail-title">{{ currentNews.title }}</h2>
              <div class="detail-time">{{ currentNews.time }}</div>
              <div class="detail-content"
                   v-html="currentNewsSafeHtml"></div>
            </div>

            <div v-else
                 class="detail-empty">
              未找到该新闻
            </div>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { newsApi } from '@/api'

export default {
  name: 'NewsInformation',
  data() {
    return {
      activeTab: 'all',
      currentPage: 1,
      itemsPerPage: 9,
      newsLoaded: false,
      newsItems: [],
      newsAssets: [
        {
          id: 1,
          time: '2023-10-24',
          cover: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=600&q=80',
          category: 'exhibition'
        },
        {
          id: 2,
          time: '2023-10-20',
          cover: 'https://images.unsplash.com/photo-1504384308090-c54be3855833?auto=format&fit=crop&w=600&q=80',
          category: 'industry'
        },
        {
          id: 3,
          time: '2023-10-15',
          cover: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=600&q=80',
          category: 'company'
        },
        {
          id: 4,
          time: '2023-10-10',
          cover: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=600&q=80',
          category: 'exhibition'
        },
        {
          id: 5,
          time: '2023-10-05',
          cover: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=600&q=80',
          category: 'industry'
        }
      ]
    };
  },
  mounted() {
    this.loadNews()
  },
  computed: {
    newsInformationCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return messages.newsInformationPage || {}
    },
    newsId() {
      const id = this.$route && this.$route.params ? this.$route.params.id : ''
      return id ? String(id) : ''
    },
    isDetail() {
      return !!this.newsId
    },
    tabs() {
      const tabs = this.newsInformationCopy.tabs || []
      return tabs.map((tab) => ({
        id: tab.id || '',
        name: tab.name || ''
      }))
    },
    newsList() {
      if (this.newsLoaded) {
        return this.newsItems
          .map((item, index) => this.mapNewsItem(item, index))
          .filter((item) => item && item.id && item.title)
          .filter((item) => this.isPublishedNews(item.raw))
      }

      const list = this.newsInformationCopy.items || []
      return this.newsAssets.map((asset, index) => {
        const item = list[index] || {}
        return {
          id: asset.id,
          title: item.title || '',
          time: asset.time || '',
          cover: asset.cover || '',
          category: asset.category || '',
          content: '',
          raw: null,
        }
      })
    },
    filteredNews() {
      if (this.activeTab === 'all') {
        return this.newsList;
      }
      return this.newsList.filter(item => item.category === this.activeTab);
    },
    totalPages() {
      return Math.ceil(this.filteredNews.length / this.itemsPerPage);
    },
    visibleNews() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredNews.slice(start, end);
    },
    currentNews() {
      if (!this.isDetail) return null
      const id = this.newsId
      if (!id) return null
      return (
        this.newsList.find((item) => String(item.id) === id) ||
        null
      )
    },
    currentNewsSafeHtml() {
      const html = this.currentNews && this.currentNews.content ? this.currentNews.content : ''
      return this.sanitizeHtml(html)
    },
    shareLink() {
      const fullPath = this.$route && this.$route.fullPath ? this.$route.fullPath : ''
      if (typeof window === 'undefined') return fullPath
      const origin = window.location && window.location.origin ? window.location.origin : ''
      return origin + fullPath
    },
  },
  watch: {
    activeTab() {
      this.currentPage = 1;
    },
    '$route.params.id'() {
      if (!this.newsLoaded) this.loadNews()
    },
  },
  methods: {
    normalizeUrl(url) {
      if (!url) return ''
      const str = String(url).trim()
      if (!str) return ''
      const noBackticks = str.replace(/`/g, '').trim()
      return noBackticks.replace(/^["']+|["']+$/g, '').trim()
    },
    normalizeCategory(value) {
      const raw = value === null || typeof value === 'undefined' ? '' : value
      if (typeof raw === 'number') {
        if (raw === 0) return 'company'
        if (raw === 1) return 'industry'
        if (raw === 2) return 'exhibition'
        return ''
      }
      const str = String(raw).trim().toLowerCase()
      if (!str) return ''
      if (str.includes('exhibition') || str.includes('展会')) return 'exhibition'
      if (str.includes('company') || str.includes('企业') || str.includes('公司')) return 'company'
      if (str.includes('industry') || str.includes('行业')) return 'industry'
      if (str === '0') return 'company'
      if (str === '1') return 'industry'
      if (str === '2') return 'exhibition'
      return str
    },
    isPublishedNews(raw) {
      if (!raw || typeof raw !== 'object') return true
      const state = raw.state
      const status = raw.status
      if (typeof state === 'number') return state === 0 || state === 1
      if (typeof status === 'number') return status === 0 || status === 1
      if (typeof state === 'string') return state === '0' || state === '1'
      if (typeof status === 'string') return status === '0' || status === '1'
      return true
    },
    mapNewsItem(raw, index) {
      const source = raw && typeof raw === 'object' ? raw : {}
      const asset = this.newsAssets[index % this.newsAssets.length] || {}

      const id =
        source.id ||
        source.newsId ||
        source.ID ||
        asset.id ||
        ''

      const title =
        source.title ||
        source.name ||
        source.newsTitle ||
        ''

      const time =
        source.createTime ||
        source.time ||
        source.date ||
        asset.time ||
        ''

      const cover =
        this.normalizeUrl(
          source.cover ||
          source.photoUrl ||
          source.imgUrl ||
          source.imageUrl ||
          source.pictureUrl ||
          source.picUrl,
        ) ||
        asset.cover ||
        ''

      const category = this.normalizeCategory(source.category || source.type || source.newsType) || asset.category || ''
      const content = source.content || source.htmlContent || source.detail || ''

      return {
        id,
        title: String(title || ''),
        time: String(time || ''),
        cover: String(cover || ''),
        category: String(category || ''),
        content: String(content || ''),
        raw: source,
      }
    },
    sanitizeHtml(html) {
      const str = String(html || '')
      if (!str) return ''
      const removedScripts = str.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      return removedScripts.replace(/\son\w+\s*=\s*(['"])[\s\S]*?\1/gi, '')
    },
    openDetail(item) {
      if (!item || !item.id) return
      this.$router.push(`/about/news/${encodeURIComponent(String(item.id))}`)
    },
    goBack() {
      this.$router.push('/about/news')
    },
    async copyShareLink() {
      const text = this.shareLink
      if (!text) return

      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          if (this.$message) this.$message.success('链接已复制')
          return
        }
      } catch (e) {
        void e
      }

      try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', 'readonly')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        if (this.$message) this.$message.success('链接已复制')
      } catch (e) {
        if (this.$message) this.$message.error('复制失败')
      }
    },
    async loadNews() {
      try {
        const result = await newsApi.selectAll()
        const ok =
          result &&
          (result.code === 200 ||
            result.success === true ||
            result.ok === true)
        const list = ok && Array.isArray(result.data) ? result.data : []
        this.newsItems = list
        this.newsLoaded = true
      } catch (e) {
        this.newsLoaded = false
        this.newsItems = []
      }
    },
  },
}
</script>

<style scoped>
.news-information-page {
  width: 100%;
}

/* Banner */
.banner-section {
  width: 100%;
  height: 546px;

  background-image: url("@/assets/news/banner.png");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-title {
  font-size: 48px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

/* News Section */
.news-section {
  min-height: 930px;
  width: 100%;
  background-color: #fff;
  padding-top: 60px; /* Adjust as needed for spacing */
  box-sizing: border-box;
}

.container {
  width: 1200px;
  margin: 0 auto;
  position: relative;
}

/* Tabs */
.nav-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  justify-content: flex-start; /* Left aligned */
}

.tab-item {
  height: 43px;
  padding: 0 30px; /* Width auto, adding padding */
  border-radius: 21px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;

  /* Default state */
  background-color: #fff;
  border: 1px solid #666666;
  color: #333;
}

.tab-item.active {
  background-color: #2ea2fa;
  border-color: #2ea2fa; /* Border disappears visually or matches bg */
  color: #fff;
  border: none; /* User requested border cancel */
}

/* News Grid */
.news-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 37px; /* (1200 - 3*375) / 2 = 87.5 / 2 = 43?  Let's use flex space-between or calc gap */
  /* Actually, with 1200px container and 375px items, 3 items take 1125px. Remaining 75px. 2 gaps -> 37.5px gap. */
  justify-content: flex-start;
  gap: 37px;
}

.news-item {
  width: 375px;
  height: 399px;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  cursor: pointer;
}

.news-cover {
  width: 375px;
  height: 233px;
  overflow: hidden;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-content {
  width: 375px;
  height: 166px;
  background-color: #f8f8f8;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.news-title {
  font-size: 18px;
  color: #000;
  font-weight: normal; /* 不加粗 */
  margin: 0;
  line-height: 1.5;

  /* Multi-line ellipsis if needed, though not specified */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-time {
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: left; /* Left aligned */
  align-self: flex-start;
}

/* Pagination */
.pagination {
  position: absolute;
  
  bottom: -10px; /* Adjust positioning */
  left: 0;
  display: flex;
  gap: 10px;
}

.page-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  background-color: #fff;
  color: #333;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
}

.page-number.active {
  background-color: #2ea2fa;
  color: #fff;
  border-color: #2ea2fa;
}

.news-detail {
  width: 100%;
}

.detail-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 30px;
}

.back-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
}

.share-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;
}

.share-input {
  width: 520px;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;
  color: #333;
  background: #fff;
}

.share-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  border: none;
  background: #2ea2fa;
  color: #fff;
  cursor: pointer;
}

.detail-body {
  width: 100%;
  background: #fff;
}

.detail-title {
  font-size: 28px;
  color: #000;
  font-weight: normal;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.detail-time {
  font-size: 14px;
  color: #999;
  margin-bottom: 24px;
}

.detail-content {
  width: 100%;
  color: #333;
  line-height: 1.8;
  word-break: break-word;
}

.detail-empty {
  font-size: 16px;
  color: #666;
}
</style>
